import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Target, RefreshCw, Clock, Hourglass, Zap, ShieldAlert, ChevronRight, Layers, ArrowLeftRight } from 'lucide-react';
import { apiClient, TradeAdvisoryReportV1, TradeAdvisorySymbolBlueprintV1 } from '../../api/client';
import { CRYPTO_BILINGUAL, CARD_TITLES_BILINGUAL } from '../../shared/constants/bilingualTitles';
import { CANONICAL_SPOT_SYMBOLS } from '../../shared/constants/watchlist';
import { formatFinancialPrice } from '../../shared/domain/formatters';
import { VelaCrossValidationDrawer } from './components/VelaCrossValidationDrawer';

const CORE7_SYMBOLS = CANONICAL_SPOT_SYMBOLS;

/**
 * Stage 088 Actionable Trade Advisory Blueprint Cockpit
 * 
 * Panoramic Matrix & Master-Detail Dual Architecture:
 * 1. Panoramic Cross-Sectional Decision Table (全景实战决策矩阵大表):
 *    - "一眼看全境": All Core-7 symbols visible simultaneously with full tactical actions,
 *      confidence, capital allocation, entry zone, invalidation stop, take profit, R:R, TD9,
 *      volume warning, and causal lag in a single panoramic table.
 *    - Fluid responsive horizontal scrolling with sticky symbol column on smaller viewports.
 * 2. Active Symbol Tactical Deep-Dive & Grounding Evidence Drawer:
 *    - Deep-dive rationale, DeMark TD guidance, volume speed dynamics, and source lineage drawer.
 * 
 * Standard Compliances:
 * - SPEC-MARKET_HUB-GLOBAL-VIEWPORT-UI-UX-001 (Global Viewport Gutter SSOT: root padding 0, fluid 100% width)
 * - SPEC-MARKET_HUB-CRYPTO-SYMBOL-MENU-UI-UX-001 (Ghost Outline Invariant, WCAG AA >= 4.5:1, zero diffuse reflection)
 * - SPEC-MARKET_HUB-BILINGUAL-TYPOGRAPHY-AND-NAV-DICTIONARY-001 (Dual-Audience bilingual hierarchy & locators)
 * - GATE-TRUTHFUL-STATE-MACHINE (Strict 5-state: loading | fresh | stale | disconnected | empty)
 * - Theme Token Contract: Zero hardcoded hex/rgba color literals; strictly driven by CSS variables.
 */
export type TimeframeId = '5m' | '15m' | '30m' | '1h' | '4h' | '1d';

interface TimeframeHorizonMeta {
  id: TimeframeId;
  label: string;
  name: string;
  en: string;
  cap: string;
  capColor: string;
  horizon: string;
  badgeText: string;
  badgeClass: string;
  desc: string;
}

const TIMEFRAME_HORIZONS: TimeframeHorizonMeta[] = [
  {
    id: '5m',
    label: '5m',
    name: '超短博弈',
    en: 'SCALP MOMENTUM',
    cap: '≤ 20.0%',
    capColor: 'var(--color-amber)',
    horizon: '30m~2h',
    badgeText: '超短博弈',
    badgeClass: 'badge-amber',
    desc: '高频微观 · 窄止损严控仓 (TTL 30m / 上限 20.0%)',
  },
  {
    id: '15m',
    label: '15m',
    name: '日内短波',
    en: 'INTRADAY',
    cap: '≤ 25.0%',
    capColor: 'var(--accent-cyan)',
    horizon: '2h~8h',
    badgeText: '日内短波',
    badgeClass: 'badge-cyan',
    desc: '日内动量突破 · 关键位博弈 (TTL 1h / 上限 25.0%)',
  },
  {
    id: '30m',
    label: '30m',
    name: '日内中线',
    en: 'INTRADAY SWING',
    cap: '≤ 30.0%',
    capColor: 'var(--color-emerald)',
    horizon: '4h~12h',
    badgeText: '日内中线',
    badgeClass: 'badge-cyan',
    desc: '日内趋势过渡 · 结构回踩确认 (TTL 2h / 上限 30.0%)',
  },
  {
    id: '1h',
    label: '1h',
    name: '小时中继',
    en: 'SWING MOMENTUM',
    cap: '≤ 30.0%',
    capColor: 'var(--color-emerald)',
    horizon: '8h~24h',
    badgeText: '小时中继',
    badgeClass: 'badge-cyan',
    desc: '稳态蓄势 · 关键结构企稳 (TTL 4h / 上限 30.0%)',
  },
  {
    id: '4h',
    label: '4h',
    name: '核心主锚点',
    en: 'PRIMARY ANCHOR',
    cap: '≤ 30.0%',
    capColor: 'var(--color-emerald)',
    horizon: '1~3天',
    badgeText: '生产主锚点 ★',
    badgeClass: 'badge-bullish',
    desc: '主波段中枢 · 核心基准主力注码 (TTL 4h / 上限 30.0%)',
  },
  {
    id: '1d',
    label: '1d',
    name: '宏观大势',
    en: 'MACRO REGIME',
    cap: '≤ 30.0%',
    capColor: 'var(--accent-cyan)',
    horizon: '1~4周',
    badgeText: '宏观大势',
    badgeClass: 'badge-cyan',
    desc: '宏观单边牛熊 · 战略大周期敞口 (TTL 24h / 上限 30.0%)',
  },
];

export interface DemarkDisplayState {
  demarkTitle: string;
  demarkGuidance: string;
  volumeWarning: string;
  isSyncing: boolean;
}

export function resolveDemarkDisplay(activeSymbolData: Partial<TradeAdvisorySymbolBlueprintV1> | null | undefined): DemarkDisplayState {
  if (!activeSymbolData) {
    return {
      demarkTitle: '数据加载中...',
      demarkGuidance: '正在拉取最新 4H 实战决策蓝图...',
      volumeWarning: '正在计算量价衰减特征...',
      isSyncing: false,
    };
  }

  // 1. disconnected has top priority
  if (activeSymbolData.status === 'disconnected') {
    return {
      demarkTitle: '数据源已断开 (DISCONNECTED)',
      demarkGuidance: '实时行情链路已中断，严禁执行开仓交易。',
      volumeWarning: '行情断开，预警挂起。',
      isSyncing: false,
    };
  }

  // 2. loading
  if (activeSymbolData.status === 'loading') {
    return {
      demarkTitle: '数据加载中...',
      demarkGuidance: '正在拉取最新 4H 实战决策蓝图...',
      volumeWarning: '正在计算量价衰减特征...',
      isSyncing: false,
    };
  }

  // 3. stale
  if (activeSymbolData.status === 'stale') {
    if (activeSymbolData.unavailable_reason === 'CELL_ANCHOR_WATERMARK_MISMATCH') {
      return {
        demarkTitle: '4H 指标时钟同步中 (SYNCING · 水印对齐中)',
        demarkGuidance: '4H K 线已闭合，多周期指标引擎正在物化最新特征切片，预计数十秒内完成自动对齐。',
        volumeWarning: '量价节拍分析挂起 (等待指标物化完成)',
        isSyncing: true,
      };
    }
    // If authentic DeMark indicators are available, preserve and display them even when macro tactical setup is pending
    if (activeSymbolData.td_setup_direction && activeSymbolData.td_setup_count !== null && activeSymbolData.td_setup_count !== undefined) {
      return {
        demarkTitle: `${activeSymbolData.td_setup_direction} SETUP ${activeSymbolData.td_setup_count}`,
        demarkGuidance: activeSymbolData.td_rhythm_guidance || '当前未触发显著 DeMark TD 序列力竭，依循 4D 趋势共振运行。',
        volumeWarning: activeSymbolData.volume_speed_warning || '量价节拍平稳，未见异常下行速度突破。',
        isSyncing: false,
      };
    }
    return {
      demarkTitle: 'DeMark 序列不可用 (STALE)',
      demarkGuidance: '指标数据时效超出阈值，战术决策已暂停，请等待数据流恢复。',
      volumeWarning: '量价节拍数据陈旧，暂无预警。',
      isSyncing: false,
    };
  }

  // 4. empty
  if (activeSymbolData.status === 'empty') {
    return {
      demarkTitle: '暂无活跃序列 (EMPTY)',
      demarkGuidance: '历史数据蓄水不足，等待 K 线样本累积。',
      volumeWarning: '数据样本不足。',
      isSyncing: false,
    };
  }

  // 5. fresh
  // ONLY fresh may display authentic count or calm fallback copy
  const hasCount = Boolean(activeSymbolData.td_setup_direction);
  return {
    demarkTitle: hasCount
      ? `${activeSymbolData.td_setup_direction} SETUP ${activeSymbolData.td_setup_count}`
      : '无活跃计数',
    demarkGuidance: activeSymbolData.td_rhythm_guidance || '当前未触发显著 DeMark TD 序列力竭，依循 4D 趋势共振运行。',
    volumeWarning: activeSymbolData.volume_speed_warning || '量价节拍平稳，未见异常下行速度突破。',
    isSyncing: false,
  };
}

export const DemarkStatusCard: React.FC<{ display: DemarkDisplayState }> = ({ display }) => {
  return React.createElement(
    'div',
    {
      'data-testid': 'demark-status-card',
      style: {
        padding: '12px 14px',
        borderRadius: '6px',
        border: display.isSyncing ? '1px solid var(--accent-cyan)' : '1px solid var(--border-subtle)',
      },
    },
    React.createElement('span', null, `DeMark 序列状态: ${display.demarkTitle}`),
    React.createElement('div', null, display.demarkGuidance)
  );
};

export const VolumeSpeedCard: React.FC<{ display: DemarkDisplayState; volumeSpeedWarning?: string | null }> = ({ display, volumeSpeedWarning }) => {
  return React.createElement(
    'div',
    {
      'data-testid': 'volume-speed-card',
      style: {
        padding: '12px 14px',
        borderRadius: '6px',
        border: volumeSpeedWarning ? '1px solid var(--color-warning)' : '1px solid var(--border-subtle)',
      },
    },
    React.createElement('span', null, '量价下行速度与衰减预警'),
    React.createElement('div', null, display.volumeWarning)
  );
};

export const TradeAdvisoryBlueprintView: React.FC = () => {
  const [report, setReport] = useState<TradeAdvisoryReportV1 | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const CADENCE_TTL_SECONDS: Record<TimeframeId, number> = {
    '5m': 1800,
    '15m': 3600,
    '30m': 7200,
    '1h': 14400,
    '4h': 14400,
    '1d': 86400,
  };

  const getInitialHashParams = (): { symbol: string; timeframe: TimeframeId } => {
    const hash = window.location.hash;
    const matchSym = hash.match(/[?&]symbol=([A-Z0-9]+)/i);
    const symUpper = matchSym ? matchSym[1].toUpperCase() : '';
    const initialSym = (CORE7_SYMBOLS as readonly string[]).includes(symUpper) ? symUpper : 'BTCUSDT';

    const matchTf = hash.match(/[?&]timeframe=([0-9a-z]+)/i);
    const tfLower = matchTf ? matchTf[1].toLowerCase() : '';
    const validTfs: TimeframeId[] = ['5m', '15m', '30m', '1h', '4h', '1d'];
    const initialTf: TimeframeId = validTfs.includes(tfLower as TimeframeId) ? (tfLower as TimeframeId) : '4h';
    return { symbol: initialSym, timeframe: initialTf };
  };

  const initialParams = useMemo(() => getInitialHashParams(), []);
  const [selectedSymbol, setSelectedSymbol] = useState<string>(initialParams.symbol);
  const [selectedTimeframe, setSelectedTimeframe] = useState<TimeframeId>(initialParams.timeframe);
  const [evidenceDrawerOpen, setEvidenceDrawerOpen] = useState<boolean>(false);
  const [lensDrawerOpen, setLensDrawerOpen] = useState<boolean>(false);
  const [lastFetchTime, setLastFetchTime] = useState<Date | null>(null);
  const reqGenRef = React.useRef<number>(0);

  // Parse symbol and timeframe from URL hash on hashchange
  useEffect(() => {
    const handleHashChange = () => {
      const { symbol: sym, timeframe: tf } = getInitialHashParams();
      setSelectedSymbol(sym);
      setSelectedTimeframe(tf);
    };
    window.addEventListener('hashchange', handleHashChange);
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, []);

  const [now, setNow] = useState<number>(Date.now());
  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, []);

  const fetchBlueprint = useCallback(async (showLoading: boolean = false, tf?: TimeframeId) => {
    const targetTf = tf || selectedTimeframe;
    const reqGen = ++reqGenRef.current;
    if (showLoading) setLoading(true);
    setError(null);
    try {
      const data = await apiClient.getTradeAdvisoryBlueprint(undefined, targetTf);
      if (reqGen === reqGenRef.current) {
        setReport(data);
        setLastFetchTime(new Date());
      }
    } catch (err: any) {
      if (reqGen === reqGenRef.current) {
        console.error('Failed to fetch trade advisory blueprint:', err);
        setError(err?.message || 'Failed to load trade advisory blueprint');
        setReport(null);
      }
    } finally {
      if (reqGen === reqGenRef.current) {
        setLoading(false);
      }
    }
  }, [selectedTimeframe]);

  useEffect(() => {
    fetchBlueprint(true, selectedTimeframe);
    // Non-hidden 30s lifecycle-safe interval
    const timer = setInterval(() => {
      fetchBlueprint(false, selectedTimeframe);
    }, 30000);
    return () => clearInterval(timer);
  }, [selectedTimeframe, fetchBlueprint]);

  const selectSymbol = (sym: string) => {
    setSelectedSymbol(sym);
    const currentHash = window.location.hash.split('?')[0] || '#/crypto/trade-advisory';
    window.location.hash = `${currentHash}?timeframe=${selectedTimeframe}&symbol=${sym}`;
  };

  const selectTimeframe = (tf: TimeframeId) => {
    setSelectedTimeframe(tf);
    const currentHash = window.location.hash.split('?')[0] || '#/crypto/trade-advisory';
    window.location.hash = `${currentHash}?timeframe=${tf}&symbol=${selectedSymbol}`;
    fetchBlueprint(false, tf);
  };

  // Overall status derived from report and all cadences
  const overallStatus = useMemo(() => {
    if (loading && !report) return 'loading';
    if (error) return 'disconnected';
    if (!report) return 'empty';
    if (report.status === 'disconnected') return 'disconnected';
    if (report.is_all_cadences_expired) return 'stale';
    if (report.status === 'loading') return 'loading';
    if (report.status === 'empty' && report.coverage_count === 0) return 'empty';
    if (report.cadences_status) {
      const anyFresh = Object.values(report.cadences_status).some(c => c && (c.status === 'fresh' || c.is_expired === false));
      if (anyFresh) return 'fresh';
    }
    if (report.status === 'stale' && !report.is_all_cadences_expired) {
      return 'fresh';
    }
    return report.status || 'empty';
  }, [loading, report, error]);

  const isClientExpired = useMemo(() => {
    // Dynamic per-symbol tactic validity window
    const symData = report?.symbols?.[selectedSymbol];
    const tacticsValidUntil = symData?.tactics_valid_until_utc;
    if (tacticsValidUntil) {
      const validUntilTs = new Date(tacticsValidUntil).getTime();
      if (!isNaN(validUntilTs)) {
        return now >= validUntilTs;
      }
    }
    // Fallback: cadence-specific TTL
    if (!report?.generated_at_utc) return false;
    const genTs = new Date(report.generated_at_utc).getTime();
    if (isNaN(genTs)) return false;
    const ttlSec = CADENCE_TTL_SECONDS[selectedTimeframe] || 14400;
    return (now - genTs) > ttlSec * 1000;
  }, [report, selectedSymbol, selectedTimeframe, now]);

  const effectiveStatus = useMemo(() => {
    if (isClientExpired && overallStatus === 'fresh') {
      return 'stale';
    }
    return overallStatus;
  }, [isClientExpired, overallStatus]);

  const activeSymbolData: TradeAdvisorySymbolBlueprintV1 | undefined = useMemo(() => {
    if (!report || !report.symbols) return undefined;
    const raw = report.symbols[selectedSymbol];
    if (!raw) return undefined;
    if (raw.status === 'fresh' && (isClientExpired || effectiveStatus === 'stale')) {
      return {
        ...raw,
        status: 'stale',
        tactical_action: 'STAND_ASIDE',
        suggested_capital_allocation_pct: '0.0',
        capital_allocation_rationale: `已自动抑制：蓝图时钟已超出 ${selectedTimeframe} 战术有效窗口`,
        unavailable_reason: raw.unavailable_reason || 'TACTICS_EXPIRED',
      };
    }
    return raw;
  }, [report, selectedSymbol, effectiveStatus, isClientExpired, selectedTimeframe]);

  // Five-State Presentation Resolver for DeMark and Volume Warning (Proposal v1.2.0 Option C / GATE-TRUTHFUL-STATE-MACHINE)
  const demarkDisplay = useMemo(() => resolveDemarkDisplay(activeSymbolData), [activeSymbolData]);

  const getActionStyle = (action?: string) => {
    switch (action) {
      case 'LONG_ACCUMULATE':
        return {
          text: 'var(--color-emerald)',
          bg: 'transparent',
          border: 'var(--color-emerald)',
          badgeClass: 'badge-bullish',
          label: '做多',
        };
      case 'SHORT_DISTRIBUTE':
        return {
          text: 'var(--color-rose)',
          bg: 'transparent',
          border: 'var(--color-rose)',
          badgeClass: 'badge-bearish',
          label: '做空',
        };
      case 'STAND_ASIDE':
      default:
        return {
          text: 'var(--text-muted)',
          bg: 'transparent',
          border: 'var(--border-subtle)',
          badgeClass: 'badge-neutral',
          label: '观望',
        };
    }
  };

  const getTierBadge = (tier?: string | null) => {
    switch (tier) {
      case 'EXEMPLARY':
        return {
          label: 'EXEMPLARY · 极佳',
          color: 'var(--color-emerald)',
          bg: 'transparent',
          border: 'var(--color-emerald)',
          badgeClass: 'badge-bullish',
        };
      case 'FAVORABLE':
        return {
          label: 'FAVORABLE · 良好',
          color: 'var(--accent-cyan)',
          bg: 'transparent',
          border: 'var(--accent-cyan)',
          badgeClass: 'badge-cyan',
        };
      case 'MARGINAL':
        return {
          label: 'MARGINAL · 临界',
          color: 'var(--color-warning)',
          bg: 'transparent',
          border: 'var(--color-warning)',
          badgeClass: 'badge-amber',
        };
      case 'UNFAVORABLE':
      default:
        return {
          label: 'UNFAVORABLE · 不利',
          color: 'var(--text-muted)',
          bg: 'transparent',
          border: 'var(--border-subtle)',
          badgeClass: 'badge-neutral',
        };
    }
  };

  const getStatusBadgeClass = (status: string) => {
    switch (status) {
      case 'fresh':
        return 'badge-bullish';
      case 'stale':
        return 'badge-amber';
      case 'loading':
        return 'badge-cyan';
      case 'disconnected':
        return 'badge-rose';
      case 'empty':
      default:
        return 'badge-neutral';
    }
  };

  const actionStyle = getActionStyle(activeSymbolData?.tactical_action);
  const tierInfo = getTierBadge(activeSymbolData?.confidence_tier);

  return (
    <div
      className="view-panel active trade-advisory-blueprint-view"
      id="view-trade-advisory"
      role="tabpanel"
      style={{
        width: '100%',
        padding: 0,
        margin: 0,
        display: 'flex',
        flexDirection: 'column',
        gap: '12px',
        boxSizing: 'border-box',
      }}
    >
      {/* 1. Standard View Header aligned with SPEC-MARKET_HUB-GLOBAL-VIEWPORT-UI-UX-001 & SPEC-MARKET_HUB-BILINGUAL-TYPOGRAPHY-AND-NAV-DICTIONARY-001 */}
      <div className="view-header">
        <div className="view-title-group bilingual-view-header">
          <div className="bilingual-header-top">
            <Target size={18} style={{ color: 'var(--accent-cyan)' }} />
            <h2 className="bilingual-header-title-zh">
              {CRYPTO_BILINGUAL.tradeAdvisory.zh}
            </h2>
            <span className="terminal-badge">STAGE 090</span>
            <span
              data-testid="aggregate-status-badge"
              className={`badge ${getStatusBadgeClass(overallStatus)}`}
            >
              {overallStatus.toUpperCase()}
            </span>
            {activeSymbolData && (
              <span
                data-testid="selected-symbol-status-badge"
                className={`badge ${getStatusBadgeClass(activeSymbolData.status)}`}
              >
                {selectedSymbol}: {activeSymbolData.status.toUpperCase()}
              </span>
            )}
          </div>
          <span className="bilingual-header-subtitle-en">{CRYPTO_BILINGUAL.tradeAdvisory.en}</span>
          <p className="view-subtitle font-mono" style={{ margin: '3px 0 0 0' }}>
            {CRYPTO_BILINGUAL.tradeAdvisory.description}
          </p>
        </div>

        <div className="view-actions" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          {report && (
            <span className="view-subtitle font-mono" style={{ fontSize: '11px' }}>
              生成: {report.generated_at_utc.replace('T', ' ').replace('Z', ' UTC')}
              {lastFetchTime && ` (拉取: ${lastFetchTime.toLocaleTimeString()})`}
            </span>
          )}
          <button
            className="toggle-btn font-mono"
            onClick={() => fetchBlueprint(true)}
            disabled={loading}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              padding: '4px 10px',
              fontSize: '12px',
              cursor: loading ? 'not-allowed' : 'pointer',
            }}
            title="点击同步最新实战交易蓝图数据"
          >
            <RefreshCw size={12} className={loading ? 'spin' : ''} />
            <span>{loading ? '同步中...' : '刷新蓝图'}</span>
          </button>
        </div>
      </div>

      {/* Disconnected / Error Banner */}
      {error && (
        <div
          style={{
            padding: '10px 14px',
            backgroundColor: 'transparent',
            border: '1px solid var(--color-rose)',
            borderRadius: '6px',
            marginBottom: '4px',
            color: 'var(--color-rose)',
            fontSize: '13px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
          }}
        >
          <ShieldAlert size={16} style={{ color: 'var(--color-rose)', flexShrink: 0 }} />
          <div>
            <strong>链路连接异常 (DISCONNECTED)</strong>: {error}。请检查后端行情生产者状态。
          </div>
        </div>
      )}

      {/* All-Cadences Expired Banner (PM Core Definition: Only when all timeframe cycles are expired) */}
      {report?.is_all_cadences_expired && (
        <div
          style={{
            padding: '10px 14px',
            backgroundColor: 'transparent',
            border: '1px solid var(--color-warning)',
            borderRadius: '6px',
            marginBottom: '4px',
            color: 'var(--color-warning)',
            fontSize: '13px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
          }}
        >
          <Clock size={16} style={{ color: 'var(--color-warning)', flexShrink: 0 }} />
          <div>
            <strong>全周期数据已过期 (ALL CADENCES EXPIRED)</strong>: 所有周期的行情与战术数据均已超出有效时限，整体蓝图建议已安全降级为观望。
          </div>
        </div>
      )}

      {/* 2. Multi-Timeframe Trading Horizon Ribbon (多时间周期交易窗口导航条) */}
      <nav
        aria-label="Multi-Timeframe Trading Horizons"
        style={{
          display: 'flex',
          gap: '10px',
          overflowX: 'auto',
          paddingBottom: '4px',
          marginBottom: '2px',
          borderBottom: '1px solid var(--border-subtle)',
        }}
      >
        {TIMEFRAME_HORIZONS.map((tf) => {
          const isSelected = selectedTimeframe === tf.id;
          const cadence = report?.cadences_status?.[tf.id];
          const cadenceStatus = cadence?.status;
          const cadenceBadgeClass =
            cadenceStatus === 'fresh'
              ? 'badge-bullish'
              : cadenceStatus === 'stale'
              ? 'badge-amber'
              : cadenceStatus === 'disconnected'
              ? 'badge-bearish'
              : 'badge-neutral';
          const cadenceBadgeText = cadence
            ? cadence.status === 'fresh'
              ? '活跃 FRESH'
              : cadence.status.toUpperCase()
            : tf.badgeText;
          return (
            <button
              key={tf.id}
              onClick={() => selectTimeframe(tf.id)}
              style={{
                flex: '1 0 170px',
                padding: '10px 14px',
                borderRadius: '8px',
                border: isSelected ? '1px solid var(--accent-cyan)' : '1px solid var(--border-subtle)',
                backgroundColor: isSelected ? 'var(--bg-surface-elevated)' : 'var(--bg-card)',
                boxShadow: isSelected ? '0 0 0 1px var(--accent-cyan)' : 'none',
                cursor: 'pointer',
                opacity: 1,
                textAlign: 'left',
                transition: 'all 0.15s ease-in-out',
                outline: 'none',
                position: 'relative',
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <span style={{ fontWeight: 800, fontSize: '15px', color: isSelected ? 'var(--accent-cyan)' : 'var(--text-primary)', fontFamily: 'var(--font-mono)' }}>
                    {tf.label}
                  </span>
                  <span style={{ fontSize: '12px', fontWeight: 700, color: 'var(--text-primary)' }}>
                    {tf.name}
                  </span>
                </div>
                <span
                  className={`badge ${cadence ? cadenceBadgeClass : tf.badgeClass}`}
                  style={{ fontSize: '9px', padding: '1px 5px', fontWeight: 700 }}
                >
                  {cadenceBadgeText}
                </span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '6px' }}>
                <span style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>
                  资金上限: <strong style={{ color: tf.capColor, fontFamily: 'var(--font-mono)', fontWeight: 800 }}>{tf.cap}</strong>
                </span>
                <span style={{ fontSize: '10px', color: 'var(--text-muted)', fontFamily: 'var(--font-mono)' }}>
                  {tf.horizon}
                </span>
              </div>
              <div style={{ fontSize: '10px', color: 'var(--text-muted)', marginTop: '4px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {tf.desc}
              </div>
            </button>
          );
        })}
      </nav>

      {/* 3. NEW: Panoramic Cross-Sectional Decision Matrix Table (全景实战决策全景大表 - 一眼看全境) */}
      <section
        className="trade-advisory-panoramic-matrix"
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '8px',
          width: '100%',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '8px' }}>
          <div className="bilingual-card-title">
            <span className="bilingual-card-title-zh" style={{ fontSize: '14px', display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Layers size={14} style={{ color: 'var(--accent-cyan)' }} />
              <span>全域 7 标的实战决策全景矩阵看板</span>
              <span className="terminal-badge" style={{ fontSize: '10px' }}>CROSS-SECTIONAL · {selectedTimeframe.toUpperCase()}</span>
            </span>
            <span className="bilingual-card-subtitle-en">ACTIONABLE TRADE ADVISORY FULL-MARKET MATRIX ({selectedTimeframe.toUpperCase()} HORIZON)</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '11px', color: 'var(--text-muted)', fontFamily: 'var(--font-mono)' }}>
            <ArrowLeftRight size={12} style={{ color: 'var(--accent-cyan)' }} />
            <span>基于 {selectedTimeframe.toUpperCase()} 核心主锚点时间窗口的全品种建议 · 点击任意行穿透至深度推演</span>
          </div>
        </div>

        {/* Scrollable Responsive Table Container */}
        <div
          className="table-scroll-wrapper"
          style={{
            width: '100%',
            overflowX: 'auto',
            borderRadius: '8px',
            border: '1px solid var(--border-subtle)',
            backgroundColor: 'var(--bg-card)',
            boxShadow: 'var(--shadow-sm)',
          }}
        >
          <table
            data-testid="panoramic-matrix-table"
            style={{
              width: '100%',
              minWidth: '1320px',
              borderCollapse: 'collapse',
              fontSize: '12px',
              textAlign: 'left',
            }}
          >
            <thead>
              <tr
                style={{
                  backgroundColor: 'var(--bg-canvas)',
                  borderBottom: '1px solid var(--border-subtle)',
                  color: 'var(--text-muted)',
                  fontSize: '11px',
                  fontWeight: 700,
                  whiteSpace: 'nowrap',
                }}
              >
                <th style={{ padding: '8px 10px', position: 'sticky', left: 0, zIndex: 3, backgroundColor: 'var(--bg-canvas)', width: '64px', minWidth: '64px' }}>
                  代码
                </th>
                <th style={{ padding: '10px 12px' }}>战术指令 (ACTION)</th>
                <th style={{ padding: '10px 12px' }}>态势与阶段 (REGIME &amp; PHASE)</th>
                <th style={{ padding: '10px 12px' }}>置信评分 (CONFIDENCE)</th>
                <th style={{ padding: '10px 12px' }}>开仓建议权重</th>
                <th style={{ padding: '10px 12px' }}>当前基准价</th>
                <th style={{ padding: '10px 12px' }}>战术入场带 (ENTRY ZONE)</th>
                <th style={{ padding: '10px 12px' }}>防守止损 (STOP)</th>
                <th style={{ padding: '10px 12px' }}>目标止盈 (TARGET)</th>
                <th style={{ padding: '10px 12px' }}>最劣R:R</th>
                <th style={{ padding: '10px 12px' }}>DeMark 序列</th>
                <th style={{ padding: '10px 12px' }}>节拍与量价预警</th>
                <th style={{ padding: '10px 12px' }}>因果时延 W</th>
                <th style={{ padding: '10px 12px', textAlign: 'center' }}>操作</th>
              </tr>
            </thead>
            <tbody>
              {CORE7_SYMBOLS.map((sym) => {
                const rawSym = report?.symbols?.[sym];
                let isSymRowExpired = false;
                if (rawSym?.tactics_valid_until_utc) {
                  const vTs = new Date(rawSym.tactics_valid_until_utc).getTime();
                  if (!isNaN(vTs)) isSymRowExpired = now >= vTs;
                } else if (report?.generated_at_utc) {
                  const gTs = new Date(report.generated_at_utc).getTime();
                  if (!isNaN(gTs)) isSymRowExpired = (now - gTs) > 600 * 1000;
                }
                const isSymExpired = (rawSym?.status === 'fresh' && isSymRowExpired) || rawSym?.status === 'stale' || rawSym?.status === 'disconnected';
                const symTacticalAction = isSymExpired ? 'STAND_ASIDE' : (rawSym?.tactical_action || 'STAND_ASIDE');
                const symAlloc = isSymExpired ? '0.0%' : (rawSym?.suggested_capital_allocation_pct ? `${rawSym.suggested_capital_allocation_pct}%` : '0.0%');
                const isSelected = selectedSymbol === sym;
                const symActionStyle = getActionStyle(symTacticalAction);
                const symTier = getTierBadge(rawSym?.confidence_tier);

                return (
                  <tr
                    key={sym}
                    data-testid={`panoramic-row-${sym}`}
                    onClick={() => selectSymbol(sym)}
                    style={{
                      borderBottom: '1px solid var(--border-subtle)',
                      backgroundColor: isSelected ? 'var(--bg-surface-elevated)' : 'transparent',
                      cursor: 'pointer',
                      transition: 'background-color 0.15s ease-in-out',
                      outline: isSelected ? '1px solid var(--accent-cyan)' : 'none',
                    }}
                  >
                    {/* 1. Sticky Symbol Column (Streamlined: clean symbol code only, reduced width) */}
                    <td
                      style={{
                        padding: '8px 10px',
                        position: 'sticky',
                        left: 0,
                        zIndex: 2,
                        backgroundColor: isSelected ? 'var(--bg-surface-elevated)' : 'var(--bg-card)',
                        borderRight: '1px solid var(--border-subtle)',
                        whiteSpace: 'nowrap',
                        width: '64px',
                        minWidth: '64px',
                      }}
                    >
                      <span
                        title={sym}
                        style={{
                          fontWeight: 700,
                          fontSize: '13px',
                          color: 'var(--text-primary)',
                          fontFamily: 'var(--font-mono)',
                          letterSpacing: '0.02em',
                        }}
                      >
                        {sym.replace('USDT', '')}
                      </span>
                    </td>

                    {/* 2. Tactical Action */}
                    <td style={{ padding: '10px 12px', whiteSpace: 'nowrap' }}>
                      <span className={`badge ${symActionStyle.badgeClass}`} style={{ fontSize: '11px', fontWeight: 700, padding: '2px 8px' }}>
                        {symTacticalAction}
                      </span>
                    </td>

                    {/* 3. Regime & Phase */}
                    <td style={{ padding: '10px 12px', whiteSpace: 'nowrap' }}>
                      <div style={{ fontSize: '11px', fontWeight: 600, color: 'var(--text-primary)' }}>
                        {rawSym?.phase_classification || '--'} · {rawSym?.regime_verdict || '--'}
                      </div>
                      <div style={{ fontSize: '10px', color: 'var(--text-muted)', maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {rawSym?.phase_description || '--'}
                      </div>
                    </td>

                    {/* 4. Confidence Score */}
                    <td style={{ padding: '10px 12px', whiteSpace: 'nowrap' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <span style={{ fontWeight: 800, fontSize: '14px', color: symTier.color, fontFamily: 'var(--font-mono)' }}>
                          {rawSym?.confidence_score ?? '--'}
                        </span>
                        <span className={`badge ${symTier.badgeClass}`} style={{ fontSize: '9px', padding: '1px 5px' }}>
                          {symTier.label.split('·')[0].trim()}
                        </span>
                      </div>
                    </td>

                    {/* 5. Allocation */}
                    <td style={{ padding: '10px 12px', whiteSpace: 'nowrap' }}>
                      <span
                        style={{
                          fontWeight: 800,
                          fontSize: '13px',
                          color: symAlloc !== '0.0%' ? 'var(--color-emerald)' : 'var(--text-muted)',
                          fontFamily: 'var(--font-mono)',
                        }}
                      >
                        {symAlloc}
                      </span>
                    </td>

                    {/* 6. Current Price */}
                    <td style={{ padding: '10px 12px', whiteSpace: 'nowrap', fontFamily: 'var(--font-mono)', fontWeight: 600 }}>
                      {rawSym?.current_price ? `$${formatFinancialPrice(rawSym.current_price, sym)}` : '--'}
                    </td>

                    {/* 7. Entry Zone */}
                    <td style={{ padding: '10px 12px', whiteSpace: 'nowrap', fontFamily: 'var(--font-mono)', color: 'var(--accent-cyan)', fontWeight: 600 }}>
                      {rawSym?.entry_low && rawSym?.entry_high
                        ? `$${formatFinancialPrice(rawSym.entry_low, sym)} ~ $${formatFinancialPrice(rawSym.entry_high, sym)}`
                        : '--'}
                    </td>

                    {/* 8. Invalidation Stop */}
                    <td style={{ padding: '10px 12px', whiteSpace: 'nowrap', fontFamily: 'var(--font-mono)', color: 'var(--color-rose)', fontWeight: 700 }}>
                      {rawSym?.invalidation_stop ? `$${formatFinancialPrice(rawSym.invalidation_stop, sym)}` : '--'}
                    </td>

                    {/* 9. Take Profit */}
                    <td style={{ padding: '10px 12px', whiteSpace: 'nowrap', fontFamily: 'var(--font-mono)', color: 'var(--color-emerald)', fontWeight: 700 }}>
                      {rawSym?.target_take_profit ? `$${formatFinancialPrice(rawSym.target_take_profit, sym)}` : '--'}
                    </td>

                    {/* 10. Worst Band R:R */}
                    <td style={{ padding: '10px 12px', whiteSpace: 'nowrap', fontFamily: 'var(--font-mono)', fontWeight: 700 }}>
                      {rawSym?.worst_band_rr ? `${rawSym.worst_band_rr} : 1` : '--'}
                    </td>

                    {/* 11. DeMark TD9 */}
                    <td style={{ padding: '10px 12px', whiteSpace: 'nowrap', fontSize: '11px' }}>
                      {rawSym?.td_setup_direction ? (
                        <span className="badge badge-cyan" style={{ fontSize: '10px' }}>
                          {rawSym.td_setup_direction} {rawSym.td_setup_count}
                        </span>
                      ) : (
                        <span style={{ color: 'var(--text-muted)' }}>--</span>
                      )}
                    </td>

                    {/* 12. Volume Speed Warning */}
                    <td style={{ padding: '10px 12px', whiteSpace: 'nowrap', fontSize: '11px' }}>
                      {rawSym?.volume_speed_warning ? (
                        <span className="badge badge-amber" style={{ fontSize: '10px' }} title={rawSym.volume_speed_warning}>
                          预警触发
                        </span>
                      ) : (
                        <span style={{ color: 'var(--text-muted)' }}>节拍平稳</span>
                      )}
                    </td>

                    {/* 13. Causal Lag */}
                    <td style={{ padding: '10px 12px', whiteSpace: 'nowrap', fontFamily: 'var(--font-mono)', fontSize: '11px' }}>
                      {rawSym?.causal_lag_seconds !== undefined && rawSym?.causal_lag_seconds !== null ? (
                        <span style={{ color: rawSym.causal_lag_seconds >= 3 ? 'var(--color-emerald)' : 'var(--color-rose)' }}>
                          {rawSym.causal_lag_seconds}s
                        </span>
                      ) : (
                        '--'
                      )}
                    </td>

                    {/* 14. Action Drilldown */}
                    <td style={{ padding: '10px 12px', textAlign: 'center', whiteSpace: 'nowrap' }}>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          selectSymbol(sym);
                        }}
                        className="toggle-btn font-mono"
                        style={{
                          fontSize: '11px',
                          padding: '2px 8px',
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '2px',
                        }}
                      >
                        <span>{isSelected ? '推演中' : '展开'}</span>
                        <ChevronRight size={11} />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>

      {/* Loading Skeleton */}
      {loading && !report && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '12px' }}>
          {[1, 2, 3, 4].map((i) => (
            <div
              key={i}
              style={{
                height: '160px',
                backgroundColor: 'var(--bg-card)',
                borderRadius: '8px',
                border: '1px solid var(--border-subtle)',
                animation: 'pulse 1.5s infinite ease-in-out',
                opacity: 0.6,
              }}
            />
          ))}
        </div>
      )}

      {/* 4. Selected Symbol Tactical Deep Dive Cockpit (选中标的深度推演与接地抽屉) */}
      {activeSymbolData && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginTop: '4px' }}>
          {/* A. Top Tactical Decision Card */}
          <section
            style={{
              backgroundColor: 'var(--bg-card)',
              borderRadius: '8px',
              border: '1px solid var(--border-subtle)',
              padding: '16px 20px',
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '16px' }}>
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <div className="bilingual-card-title">
                    <span className="bilingual-card-title-zh" style={{ fontSize: '13px' }}>
                      战术决策总览 ({selectedSymbol})
                    </span>
                    <span className="bilingual-card-subtitle-en">TACTICAL DECISION</span>
                  </div>
                  <span
                    data-testid="selected-symbol-inline-status"
                    className={`badge ${getStatusBadgeClass(activeSymbolData.status)}`}
                    style={{ fontSize: '10px', padding: '1px 5px' }}
                  >
                    {activeSymbolData.status.toUpperCase()}
                  </span>
                </div>
                {activeSymbolData.unavailable_reason && (
                  <div
                    data-testid="symbol-unavailable-reason-label"
                    style={{
                      fontSize: '12px',
                      color: 'var(--color-warning)',
                      marginTop: '4px',
                      fontWeight: 600,
                      fontFamily: 'var(--font-mono)',
                    }}
                  >
                    不可用原因 (Reason): {activeSymbolData.unavailable_reason}
                  </div>
                )}
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginTop: '8px' }}>
                  <span
                    className={`badge ${actionStyle.badgeClass}`}
                    style={{
                      fontSize: '16px',
                      fontWeight: 800,
                      padding: '4px 14px',
                      letterSpacing: '0.04em',
                    }}
                  >
                    {activeSymbolData.tactical_action}
                  </span>
                  <div>
                    <div style={{ fontSize: '14px', fontWeight: 600, color: 'var(--text-primary)' }}>
                      {activeSymbolData.phase_classification} · {activeSymbolData.regime_verdict}
                    </div>
                    <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
                      {activeSymbolData.phase_description}
                    </div>
                  </div>
                </div>
              </div>

              {/* Confidence Score & Capital Allocation */}
              <div style={{ display: 'flex', gap: '16px', alignItems: 'center', flexWrap: 'wrap' }}>
                {/* Confidence Score */}
                <div
                  style={{
                    textAlign: 'center',
                    padding: '8px 16px',
                    borderRadius: '6px',
                    backgroundColor: 'var(--bg-canvas)',
                    border: '1px solid var(--border-subtle)',
                    minWidth: '130px',
                  }}
                >
                  <div style={{ fontSize: '10px', color: 'var(--text-muted)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                    置信评分 (CONFIDENCE)
                  </div>
                  <div style={{ fontSize: '22px', fontWeight: 800, color: tierInfo.color, marginTop: '2px', fontFamily: 'var(--font-mono)' }}>
                    {activeSymbolData.confidence_score ?? '--'}
                    <span style={{ fontSize: '11px', fontWeight: 500, color: 'var(--text-muted)' }}>/100</span>
                  </div>
                  <div style={{ marginTop: '2px' }}>
                    <span className={`badge ${tierInfo.badgeClass}`} style={{ fontSize: '10px', padding: '1px 6px' }}>
                      {tierInfo.label}
                    </span>
                  </div>
                </div>

                {/* Capital Allocation */}
                <div
                  style={{
                    textAlign: 'center',
                    padding: '8px 16px',
                    borderRadius: '6px',
                    backgroundColor: 'var(--bg-canvas)',
                    border: '1px solid var(--border-subtle)',
                    minWidth: '150px',
                  }}
                >
                  <div style={{ fontSize: '10px', color: 'var(--text-muted)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                    建议开仓权重 (ALLOCATION)
                  </div>
                  <div
                    style={{
                      fontSize: '22px',
                      fontWeight: 800,
                      color: activeSymbolData.suggested_capital_allocation_pct !== '0.0' ? 'var(--color-emerald)' : 'var(--text-muted)',
                      marginTop: '2px',
                      fontFamily: 'var(--font-mono)',
                    }}
                  >
                    {activeSymbolData.suggested_capital_allocation_pct}%
                  </div>
                  <div style={{ fontSize: '10px', color: 'var(--text-secondary)', marginTop: '2px', maxWidth: '170px' }}>
                    {activeSymbolData.capital_allocation_rationale}
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* B. Actionable Price Geometry Grid */}
          <section
            style={{
              backgroundColor: 'var(--bg-card)',
              borderRadius: '8px',
              border: '1px solid var(--border-subtle)',
              padding: '16px 20px',
            }}
          >
            <div className="bilingual-card-title" style={{ marginBottom: '12px' }}>
              <span className="bilingual-card-title-zh" style={{ fontSize: '13px' }}>
                {CARD_TITLES_BILINGUAL.actionableGeometry?.zh || '入场止损止盈几何空间'}
              </span>
              <span className="bilingual-card-subtitle-en">
                {CARD_TITLES_BILINGUAL.actionableGeometry?.en || 'ACTIONABLE PRICE GEOMETRY'}
              </span>
            </div>

            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
                gap: '10px',
              }}
            >
              {/* Current Price */}
              <div style={{ padding: '10px 12px', borderRadius: '6px', backgroundColor: 'var(--bg-canvas)', border: '1px solid var(--border-subtle)' }}>
                <div style={{ fontSize: '10px', color: 'var(--text-muted)', fontWeight: 600 }}>当前基准价 (CURRENT PRICE)</div>
                <div style={{ fontSize: '16px', fontWeight: 700, marginTop: '2px', color: 'var(--text-primary)', fontFamily: 'var(--font-mono)' }}>
                  {activeSymbolData.current_price ? `$${formatFinancialPrice(activeSymbolData.current_price, selectedSymbol)}` : '--'}
                </div>
              </div>

              {/* Entry Zone */}
              <div style={{ padding: '10px 12px', borderRadius: '6px', backgroundColor: 'var(--bg-canvas)', border: '1px solid var(--border-subtle)' }}>
                <div style={{ fontSize: '10px', color: 'var(--accent-cyan)', fontWeight: 600 }}>战术入场带 (ENTRY ZONE)</div>
                <div style={{ fontSize: '15px', fontWeight: 700, marginTop: '2px', color: 'var(--accent-cyan)', fontFamily: 'var(--font-mono)' }}>
                  {activeSymbolData.entry_low && activeSymbolData.entry_high
                    ? `$${formatFinancialPrice(activeSymbolData.entry_low, selectedSymbol)} ~ $${formatFinancialPrice(activeSymbolData.entry_high, selectedSymbol)}`
                    : '--'}
                </div>
              </div>

              {/* Invalidation Stop */}
              <div style={{ padding: '10px 12px', borderRadius: '6px', backgroundColor: 'transparent', border: '1px solid var(--badge-bearish-border, var(--border-subtle))' }}>
                <div style={{ fontSize: '10px', color: 'var(--color-rose)', fontWeight: 600 }}>结构性防守止损 (INVALIDATION STOP)</div>
                <div style={{ fontSize: '16px', fontWeight: 700, marginTop: '2px', color: 'var(--color-rose)', fontFamily: 'var(--font-mono)' }}>
                  {activeSymbolData.invalidation_stop ? `$${formatFinancialPrice(activeSymbolData.invalidation_stop, selectedSymbol)}` : '--'}
                </div>
              </div>

              {/* Target Take Profit */}
              <div style={{ padding: '10px 12px', borderRadius: '6px', backgroundColor: 'transparent', border: '1px solid var(--badge-bullish-border, var(--border-subtle))' }}>
                <div style={{ fontSize: '10px', color: 'var(--color-emerald)', fontWeight: 600 }}>目标止盈 (TAKE PROFIT)</div>
                <div style={{ fontSize: '16px', fontWeight: 700, marginTop: '2px', color: 'var(--color-emerald)', fontFamily: 'var(--font-mono)' }}>
                  {activeSymbolData.target_take_profit ? `$${formatFinancialPrice(activeSymbolData.target_take_profit, selectedSymbol)}` : '--'}
                </div>
              </div>

              {/* Worst Band RR */}
              <div style={{ padding: '10px 12px', borderRadius: '6px', backgroundColor: 'var(--bg-canvas)', border: '1px solid var(--border-subtle)' }}>
                <div style={{ fontSize: '10px', color: 'var(--text-muted)', fontWeight: 600 }}>最劣端盈亏比 (WORST-BAND R:R)</div>
                <div style={{ fontSize: '16px', fontWeight: 700, marginTop: '2px', color: 'var(--text-primary)', fontFamily: 'var(--font-mono)' }}>
                  {activeSymbolData.worst_band_rr ? `${activeSymbolData.worst_band_rr} : 1` : '--'}
                </div>
              </div>

              {/* Trailing Breakeven */}
              <div style={{ padding: '10px 12px', borderRadius: '6px', backgroundColor: 'var(--bg-canvas)', border: '1px solid var(--border-subtle)' }}>
                <div style={{ fontSize: '10px', color: 'var(--text-muted)', fontWeight: 600 }}>保本移动止损线 (TRAILING BREAKEVEN)</div>
                <div style={{ fontSize: '16px', fontWeight: 700, marginTop: '2px', color: 'var(--text-primary)', fontFamily: 'var(--font-mono)' }}>
                  {activeSymbolData.trailing_breakeven_trigger ? `$${formatFinancialPrice(activeSymbolData.trailing_breakeven_trigger, selectedSymbol)}` : '--'}
                </div>
              </div>

              {/* Scale Out Target */}
              <div style={{ padding: '10px 12px', borderRadius: '6px', backgroundColor: 'var(--bg-canvas)', border: '1px solid var(--border-subtle)' }}>
                <div style={{ fontSize: '10px', color: 'var(--text-muted)', fontWeight: 600 }}>首批减仓目标 (SCALE-OUT TARGET)</div>
                <div style={{ fontSize: '16px', fontWeight: 700, marginTop: '2px', color: 'var(--text-primary)', fontFamily: 'var(--font-mono)' }}>
                  {activeSymbolData.scale_out_target ? `$${formatFinancialPrice(activeSymbolData.scale_out_target, selectedSymbol)}` : '--'}
                </div>
              </div>

              {/* Time Decay Horizon */}
              <div style={{ padding: '10px 12px', borderRadius: '6px', backgroundColor: 'var(--bg-canvas)', border: '1px solid var(--border-subtle)' }}>
                <div style={{ fontSize: '10px', color: 'var(--text-muted)', fontWeight: 600 }}>时间衰减视界 (TIME DECAY)</div>
                <div style={{ fontSize: '16px', fontWeight: 700, marginTop: '2px', color: 'var(--text-primary)', fontFamily: 'var(--font-mono)' }}>
                  {activeSymbolData.time_decay_horizon_bars ? `${activeSymbolData.time_decay_horizon_bars} 根 4H K线` : '--'}
                </div>
              </div>

              {/* 4H Tactics Validity Window Countdown */}
              <div style={{ padding: '10px 12px', borderRadius: '6px', backgroundColor: 'var(--bg-canvas)', border: '1px solid var(--border-subtle)' }}>
                <div style={{ fontSize: '10px', color: 'var(--text-muted)', fontWeight: 600 }}>4H 战术有效窗口 (4H TACTICS VALIDITY)</div>
                <div style={{ fontSize: '16px', fontWeight: 700, marginTop: '2px', color: 'var(--accent-cyan)', fontFamily: 'var(--font-mono)' }}>
                  {activeSymbolData.tactics_valid_until_utc ? (
                    (() => {
                      const validTs = new Date(activeSymbolData.tactics_valid_until_utc).getTime();
                      const remSec = Math.max(0, Math.floor((validTs - now) / 1000));
                      const hrs = Math.floor(remSec / 3600);
                      const mins = Math.floor((remSec % 3600) / 60);
                      return remSec > 0 ? `${hrs}h ${mins}m 剩余` : '已过期 (STALE)';
                    })()
                  ) : '--'}
                </div>
              </div>

              {/* Relative Entry Distance */}
              <div style={{ padding: '10px 12px', borderRadius: '6px', backgroundColor: 'var(--bg-canvas)', border: '1px solid var(--border-subtle)' }}>
                <div style={{ fontSize: '10px', color: 'var(--text-muted)', fontWeight: 600 }}>相对入场中轨偏离 (ENTRY DISTANCE)</div>
                <div style={{ fontSize: '16px', fontWeight: 700, marginTop: '2px', color: 'var(--text-primary)', fontFamily: 'var(--font-mono)' }}>
                  {(() => {
                    if (activeSymbolData.relative_entry_distance_pct) {
                      const raw = activeSymbolData.relative_entry_distance_pct.trim();
                      const prefix = (!raw.startsWith('+') && !raw.startsWith('-') && raw !== '0' && raw !== '0.0' && raw !== '0.00') ? '+' : '';
                      return `${prefix}${raw}%`;
                    }
                    return '--';
                  })()}
                </div>
              </div>
            </div>
          </section>

          {/* C. DeMark TD9 & Volume Speed Rhythm Card */}
          <section
            style={{
              backgroundColor: 'var(--bg-card)',
              borderRadius: '8px',
              border: '1px solid var(--border-subtle)',
              padding: '16px 20px',
            }}
          >
            <div className="bilingual-card-title" style={{ marginBottom: '12px' }}>
              <span className="bilingual-card-title-zh" style={{ fontSize: '13px' }}>
                DeMark 序列力竭与量价节拍预警
              </span>
              <span className="bilingual-card-subtitle-en">
                DEMARK TD9 EXHAUSTION & VOLUME RHYTHM WARNING
              </span>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '10px' }}>
              {/* TD9 Setup Guidance */}
              {/* DeMark TD9 / Exhaustion */}
              <div
                data-testid="demark-status-card"
                style={{
                  padding: '12px 14px',
                  borderRadius: '6px',
                  backgroundColor: 'var(--bg-canvas)',
                  border: demarkDisplay.isSyncing ? '1px solid var(--accent-cyan)' : '1px solid var(--border-subtle)',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px' }}>
                  <Hourglass size={14} style={{ color: demarkDisplay.isSyncing ? 'var(--accent-cyan)' : 'var(--text-muted)' }} />
                  <span style={{ fontWeight: 700, fontSize: '12px', color: 'var(--text-primary)' }}>
                    DeMark 序列状态: {demarkDisplay.demarkTitle}
                  </span>
                </div>
                <div style={{ fontSize: '12px', color: 'var(--text-secondary)', lineHeight: 1.5 }}>
                  {demarkDisplay.demarkGuidance}
                </div>
              </div>

              {/* Volume Speed Warning */}
              <div
                data-testid="volume-speed-card"
                style={{
                  padding: '12px 14px',
                  borderRadius: '6px',
                  backgroundColor: 'transparent',
                  border: activeSymbolData.volume_speed_warning ? '1px solid var(--color-warning)' : '1px solid var(--border-subtle)',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px' }}>
                  <Zap size={14} style={{ color: activeSymbolData.volume_speed_warning ? 'var(--color-warning)' : 'var(--text-muted)' }} />
                  <span style={{ fontWeight: 700, fontSize: '12px', color: activeSymbolData.volume_speed_warning ? 'var(--color-warning)' : 'var(--text-primary)' }}>
                    量价下行速度与衰减预警
                  </span>
                </div>
                <div style={{ fontSize: '12px', color: 'var(--text-secondary)', lineHeight: 1.5 }}>
                  {demarkDisplay.volumeWarning}
                </div>
              </div>
            </div>
          </section>

          {/* D. Grounding Sentinel & Lineage Status Bar */}
          <section
            style={{
              backgroundColor: 'var(--bg-card)',
              borderRadius: '8px',
              border: '1px solid var(--border-subtle)',
              padding: '12px 16px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: '10px',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '14px', flexWrap: 'wrap' }}>
              <div>
                <span style={{ fontSize: '11px', color: 'var(--text-muted)', fontWeight: 600 }}>哨兵状态 (SENTINEL): </span>
                <span
                  style={{
                    fontSize: '11px',
                    fontWeight: 700,
                    fontFamily: 'var(--font-mono)',
                    color: activeSymbolData.grounding_sentinel_status === 'ACTIVE_MONITORING' ? 'var(--color-emerald)' : 'var(--color-rose)',
                  }}
                >
                  {activeSymbolData.grounding_sentinel_status}
                </span>
              </div>
              <div>
                <span style={{ fontSize: '11px', color: 'var(--text-muted)', fontWeight: 600 }}>4H K线时延: </span>
                <span style={{ fontSize: '11px', fontWeight: 600, color: 'var(--text-primary)', fontFamily: 'var(--font-mono)' }}>
                  {activeSymbolData.bar_age_seconds !== undefined && activeSymbolData.bar_age_seconds !== null
                    ? `${activeSymbolData.bar_age_seconds}s`
                    : '--'}
                </span>
              </div>
              <div>
                <span style={{ fontSize: '11px', color: 'var(--text-muted)', fontWeight: 600 }}>报价时延: </span>
                <span style={{ fontSize: '11px', fontWeight: 600, color: 'var(--text-primary)', fontFamily: 'var(--font-mono)' }}>
                  {activeSymbolData.quote_age_seconds !== undefined && activeSymbolData.quote_age_seconds !== null
                    ? `${activeSymbolData.quote_age_seconds}s`
                    : '--'}
                </span>
              </div>
              <div>
                <span style={{ fontSize: '11px', color: 'var(--text-muted)', fontWeight: 600 }}>因果时延 W: </span>
                <span style={{ fontSize: '11px', fontWeight: 600, color: 'var(--text-primary)', fontFamily: 'var(--font-mono)' }}>
                  {activeSymbolData.causal_lag_seconds !== undefined && activeSymbolData.causal_lag_seconds !== null
                    ? `${activeSymbolData.causal_lag_seconds}s (>=3s PASS)`
                    : '--'}
                </span>
              </div>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <button
                type="button"
                data-testid="lens-drawer-toggle-btn"
                className="toggle-btn font-mono"
                onClick={() => setLensDrawerOpen(true)}
                style={{
                  padding: '4px 12px',
                  fontSize: '11px',
                  fontWeight: 700,
                  cursor: 'pointer',
                  backgroundColor: 'var(--color-accent-subtle, rgba(46, 117, 89, 0.2))',
                  border: '1px solid var(--color-accent, #38ef7d)',
                  color: 'var(--color-accent, #38ef7d)',
                  borderRadius: '4px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                }}
              >
                <span>🔍</span>
                <span>走势与决策透镜 (LENS)</span>
              </button>

              <button
                data-testid="evidence-drawer-toggle"
                className="toggle-btn font-mono"
                onClick={() => setEvidenceDrawerOpen(!evidenceDrawerOpen)}
                style={{
                  padding: '4px 10px',
                  fontSize: '11px',
                  cursor: 'pointer',
                }}
              >
                {evidenceDrawerOpen ? '收起底层接地证据 ▲' : '查看底层接地证据 (DRAWER) ▼'}
              </button>
            </div>
          </section>

          {/* E. Expandable Grounding Evidence Drawer */}
          {evidenceDrawerOpen && (
            <section
              data-testid="evidence-drawer"
              style={{
                backgroundColor: 'var(--bg-canvas)',
                borderRadius: '8px',
                border: '1px solid var(--border-subtle)',
                padding: '16px 20px',
              }}
            >
              <div className="bilingual-card-title" style={{ marginBottom: '10px' }}>
                <span className="bilingual-card-title-zh" style={{ fontSize: '13px' }}>
                  {CARD_TITLES_BILINGUAL.groundingEvidenceDrawer?.zh || '底层接地事实与溯源抽屉'}
                </span>
                <span className="bilingual-card-subtitle-en">
                  {CARD_TITLES_BILINGUAL.groundingEvidenceDrawer?.en || 'GROUNDING EVIDENCE DRAWER'}
                </span>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '8px', marginBottom: '12px' }}>
                <div style={{ fontSize: '11px', color: 'var(--text-secondary)', fontFamily: 'var(--font-mono)' }}>
                  <strong>Source Lineage:</strong> {activeSymbolData.source_lineage || 'desk_strategist:trade_advisory_synthesizer'}
                </div>
                <div style={{ fontSize: '11px', color: 'var(--text-secondary)', fontFamily: 'var(--font-mono)' }}>
                  <strong>Close Time UTC:</strong> {activeSymbolData.close_time_utc || '--'}
                </div>
                <div style={{ fontSize: '11px', color: 'var(--text-secondary)', fontFamily: 'var(--font-mono)' }}>
                  <strong>Ingested At UTC:</strong> {activeSymbolData.ingested_at_utc || '--'}
                </div>
                <div style={{ fontSize: '11px', color: 'var(--text-secondary)', fontFamily: 'var(--font-mono)' }}>
                  <strong>Quote Timestamp UTC:</strong> {activeSymbolData.quote_timestamp_utc || '--'}
                </div>
                <div style={{ fontSize: '11px', color: 'var(--text-secondary)', fontFamily: 'var(--font-mono)' }}>
                  <strong>Unavailable Reason:</strong> {activeSymbolData.unavailable_reason || 'None (Setup Active)'}
                </div>
              </div>

              <details style={{ marginTop: '8px' }}>
                <summary style={{ cursor: 'pointer', fontSize: '11px', fontWeight: 600, color: 'var(--text-muted)' }}>
                  展开原始 JSON 契约凭证 (Raw Contract Payload)
                </summary>
                <pre
                  style={{
                    marginTop: '6px',
                    padding: '10px 12px',
                    borderRadius: '6px',
                    backgroundColor: 'var(--bg-card)',
                    border: '1px solid var(--border-subtle)',
                    fontSize: '11px',
                    overflowX: 'auto',
                    color: 'var(--text-primary)',
                    fontFamily: 'var(--font-mono)',
                  }}
                >
                  {JSON.stringify(activeSymbolData, null, 2)}
                </pre>
              </details>
            </section>
          )}
        </div>
      )}

      {/* F. LuxAlgo/Vela Cross-Validation Lens Drawer */}
      <VelaCrossValidationDrawer
        isOpen={lensDrawerOpen}
        onClose={() => setLensDrawerOpen(false)}
        symbol={selectedSymbol}
        timeframe={selectedTimeframe}
        blueprint={activeSymbolData}
        cadenceStatus={report?.cadences_status?.[selectedTimeframe]}
        onSelectTimeframe={selectTimeframe}
      />
    </div>
  );
};

export default TradeAdvisoryBlueprintView;
